import { useCallback, useEffect } from 'react';
import { FOTO_SOORTEN, type Foto, type FotoSoort } from '@/types/domain';
import { bytesLeesbaar, datumNL } from '@/utils/format';
import { SluitIcoon } from '@/components/layout/Iconen';
import './form.css';

interface FotoViewerProps {
  fotos: Foto[];
  /** Index van de foto die getoond wordt. */
  index: number;
  /** Tijdelijke weergave-links per foto-id. */
  urls: Record<string, string>;
  bijSluiten: () => void;
  bijWisselen: (nieuweIndex: number) => void;
  /**
   * Soort of toelichting aanpassen, hier ter plekke.
   *
   * Weglaten maakt de weergave alleen-lezen; dat is wat de meekijkstand van
   * de gemeente nodig heeft.
   */
  bijWijziging?: (id: string, velden: Partial<Pick<Foto, 'soort' | 'toelichting'>>) => void;
}

/**
 * Foto op volle grootte. Een miniatuur van 100 pixels is genoeg om te zien
 * dát er een foto is, maar niet om te beoordelen of er tegels liggen — en dat
 * is nu juist waar het om gaat, zeker als de gemeente meekijkt.
 */
export function FotoViewer({
  fotos,
  index,
  urls,
  bijSluiten,
  bijWisselen,
  bijWijziging,
}: FotoViewerProps): JSX.Element | null {
  const foto = fotos[index];

  const vorige = useCallback(() => {
    bijWisselen((index - 1 + fotos.length) % fotos.length);
  }, [index, fotos.length, bijWisselen]);

  const volgende = useCallback(() => {
    bijWisselen((index + 1) % fotos.length);
  }, [index, fotos.length, bijWisselen]);

  useEffect(() => {
    const bijToets = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      // Het formulierpaneel luistert ook op Escape. Zonder dit zou één druk op
      // de toets de foto én het hele formulier sluiten, en ben je je plek kwijt.
      // Afvangen in de capture-fase komt vóór de luisteraar van het paneel.
      event.stopPropagation();

      if (event.key === 'Escape') bijSluiten();
      if (event.key === 'ArrowLeft') vorige();
      if (event.key === 'ArrowRight') volgende();
    };

    document.addEventListener('keydown', bijToets, true);
    return () => document.removeEventListener('keydown', bijToets, true);
  }, [bijSluiten, vorige, volgende]);

  if (!foto) return null;

  return (
    <div
      className="fotoviewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Foto ${foto.soort}`}
      onClick={bijSluiten}
    >
      <button
        type="button"
        className="fotoviewer__sluit"
        onClick={bijSluiten}
        aria-label="Foto sluiten"
      >
        <SluitIcoon />
      </button>

      {fotos.length > 1 && (
        <>
          <button
            type="button"
            className="fotoviewer__pijl fotoviewer__pijl--links"
            onClick={(event) => {
              event.stopPropagation();
              vorige();
            }}
            aria-label="Vorige foto"
          >
            &#8249;
          </button>
          <button
            type="button"
            className="fotoviewer__pijl fotoviewer__pijl--rechts"
            onClick={(event) => {
              event.stopPropagation();
              volgende();
            }}
            aria-label="Volgende foto"
          >
            &#8250;
          </button>
        </>
      )}

      {urls[foto.id] ? (
        <img
          className="fotoviewer__afbeelding"
          src={urls[foto.id]}
          alt={`${foto.soort}: ${foto.bestandsnaam}`}
          onClick={(event) => event.stopPropagation()}
        />
      ) : (
        <div className="fotoviewer__laden">Foto wordt geladen...</div>
      )}

      <div className="fotoviewer__balk" onClick={(event) => event.stopPropagation()}>
        <div className="fotoviewer__regel">
          <span>{datumNL(foto.toegevoegdOp)}</span>
          <span>{bytesLeesbaar(foto.grootteBytes)}</span>
          {fotos.length > 1 && (
            <span className="fotoviewer__teller">
              {index + 1} van {fotos.length}
            </span>
          )}
        </div>
      </div>

      {/*
        Soort en toelichting zijn hier direct te wijzigen. Je kijkt naar het
        beeld op het moment dat je bedenkt wat erop staat; dat afsluiten om
        elders een potloodje te zoeken is een omweg die niemand neemt.
      */}
      {bijWijziging && (
      <div className="fotoviewer__bewerk" onClick={(event) => event.stopPropagation()}>
        <div className="fotoviewer__soorten" role="group" aria-label="Soort foto">
          {FOTO_SOORTEN.map((optie) => (
            <button
              key={optie}
              type="button"
              className={`fotoviewer__soort${foto.soort === optie ? ' fotoviewer__soort--actief' : ''}`}
              aria-pressed={foto.soort === optie}
              onClick={() => bijWijziging(foto.id, { soort: optie as FotoSoort })}
            >
              {optie}
            </button>
          ))}
        </div>

        <textarea
          className="fotoviewer__toelichting"
          value={foto.toelichting}
          placeholder="Wat staat erop? Bijvoorbeeld: regenpijp links naast de deur."
          aria-label="Toelichting bij deze foto"
          rows={2}
          onChange={(event) => bijWijziging(foto.id, { toelichting: event.target.value })}
        />
      </div>
      )}

      {/* Alleen lezen: de toelichting hoort nog steeds bij het beeld. */}
      {!bijWijziging && foto.toelichting && (
        <div className="fotoviewer__bewerk" onClick={(event) => event.stopPropagation()}>
          <p>{foto.toelichting}</p>
        </div>
      )}
    </div>
  );
}
